import { OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router, RouterLink } from '@angular/router';
import { ClickOutsideDirective } from '@core/directives/click-outside.directive';
import { SvgIconComponent } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { GamesService } from '@core/services/games.service';
import { CaseBattlesService } from '@features/case-battles/case-battles.service';
import { filter } from 'rxjs';
import { GamesEnum } from '@core/enums/games.enum';
import { KingService } from '@old-app/king/king.service';
import { KingNewService } from '@features/king-new/king-new.service';
import { CoinflipDeprecatedService } from '@old-app/coinflip/coinflip.service';
import { JackpotHrService } from '@old-app/jackpot/jackpot-hr.service';
import { HeaderFavoriteGame } from '../interfaces/header-games.interface';
import { HeaderFavoriteGameComponent } from '../header-favorite-game/header-favorite-game.component';

const GAME_BOOKMARKS_KEY = 'tf2_game_bookmarks';
const MAX_GAMES = 4;
const gamesWithValues: (keyof typeof GamesEnum)[] = [
  'Jackpot',
  'Case Battles',
  'Coinflip',
  'King',
  'King New',
];

@Component({
  selector: 'app-header-games',
  imports: [
    OverlayModule,
    SvgIconComponent,
    RouterLink,
    ClickOutsideDirective,
    HeaderFavoriteGameComponent,
  ],
  templateUrl: './header-games.component.html',
  styleUrl: './header-games.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GamesAreaComponent {
  gamesService = inject(GamesService);
  toastService = inject(ToastrService);
  caseBattlesService = inject(CaseBattlesService);
  kingService = inject(KingService);
  kingNewService = inject(KingNewService);
  coinflipService = inject(CoinflipDeprecatedService);
  jackpotService = inject(JackpotHrService);

  router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  games = signal<HeaderFavoriteGame[]>(
    (() => {
      const baseGames: HeaderFavoriteGame[] = this.gamesService.gamesList().map((game) => {
        const isGameWithValue = gamesWithValues.includes(game.name);
        return {
          ...game,
          isActive: isGameWithValue,
          showValue: isGameWithValue,
        };
      });

      // Find index of Upgrade game and insert Upgrader New after it
      const upgradeIndex = baseGames.findIndex(g => g.name === 'Upgrade');
      if (upgradeIndex !== -1) {
        const upgraderNew = {
          name: 'Upgrader New' as keyof typeof GamesEnum,
          description: 'Upgrade your items',
          icon: 'assets/icons/games/upgrade.svg',
          link: 'upgrader-new',
          banner: 'assets/images/home/upgrader.jpg',
          isActive: false,
          showValue: false,
        } as HeaderFavoriteGame;
        baseGames.splice(upgradeIndex + 1, 0, upgraderNew);
      }

      return baseGames;
    })()
  );
  isOpenGamesPopover = false;

  selectedGames = computed(() => {
    return this.games().filter((game) => game.isActive);
  });

  constructor() {
    this.initGameBookmarks();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.isOpenGamesPopover) {
          this.isOpenGamesPopover = false;
        }
      });
  }

  initGameBookmarks() {
    const gameBookmarks = localStorage.getItem(GAME_BOOKMARKS_KEY);
    const savedGames: unknown = gameBookmarks
      ? JSON.parse(gameBookmarks)
      : null;

    if (Array.isArray(savedGames)) {
      this.games.update((games) =>
        games.map((game) => ({
          ...game,
          isActive: savedGames?.includes(game.name) ?? false,
        }))
      );
    }
  }

  setGames(game: HeaderFavoriteGame, idx: number) {
    const isLimit = this.selectedGames().length >= MAX_GAMES && !game.isActive;

    if (isLimit) {
      this.toastService.error('You can only select up to 4 games');
      return;
    }

    this.games.update((games) => {
      const newGames = [...games];
      newGames[idx] = { ...game, isActive: !game.isActive };
      return newGames;
    });

    localStorage.setItem(
      GAME_BOOKMARKS_KEY,
      JSON.stringify(this.selectedGames().map((g) => g.name))
    );
  }
}
